// =============================================================================
// Exchange rates — hybrid live + fallback.
//
// On first import, tries to load fresh rates from a free public API
// (open.er-api.com) and caches them in localStorage for 24h. If the API call
// fails (offline / blocked / down), falls back silently to the hard-coded
// April 2026 rates baked into this file.
//
// All rates are quoted as "units of currency per 1 USD".
// To convert FROM ccy A TO ccy B:
//   value_in_B = value_in_A * (rates[B] / rates[A])
// =============================================================================

// Hard-coded fallback rates (mid-market, approx. April 2026). Keep these for
// offline/no-API scenarios. Live rates layer on top of this.
export const FALLBACK_RATES = {
  USD: 1.00,

  // Eurozone + Europe
  EUR: 0.93,
  GBP: 0.79,
  CHF: 0.91,
  SEK: 10.6,
  NOK: 10.8,
  DKK: 6.94,
  PLN: 4.00,
  CZK: 23.5,
  HUF: 360,
  RON: 4.60,
  BGN: 1.82,

  // Americas
  CAD: 1.36,
  MXN: 17.5,
  BRL: 5.0,

  // Asia-Pacific
  AUD: 1.52,
  NZD: 1.65,
  JPY: 150.5,
  HKD: 7.81,
  SGD: 1.34,
  MYR: 4.70,
  IDR: 16200,
  PHP: 56.0,
  VND: 25000,

  // Middle East / Africa
  ILS: 3.70,
  TRY: 33.0,
  ZAR: 18.5,
  MAD: 9.95,

  // South Asia
  INR: 83.5,
};

// Mutable current rates. Starts as a copy of fallback. Replaced on successful
// live fetch. Components read from this via getRate() / convert().
let CURRENT_RATES = { ...FALLBACK_RATES };
let LAST_FETCHED = null;          // Date.now() of most recent successful fetch
let LAST_SOURCE = 'fallback';     // 'fallback' | 'cache' | 'live'

const CACHE_KEY = 'etsy-calc-fx-cache-v1';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;     // refresh once per day max
const API_URL = 'https://open.er-api.com/v6/latest/USD';

// Listeners — components subscribe to be notified when rates change
const listeners = new Set();
function notify() {
  for (const fn of listeners) {
    try { fn(); } catch (e) { /* swallow */ }
  }
}

export function subscribeRates(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Try to hydrate from localStorage on module load (browser only)
if (typeof window !== 'undefined') {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (raw) {
      const cached = JSON.parse(raw);
      if (cached && cached.rates && typeof cached.fetchedAt === 'number') {
        // Even if expired, use it as a starting point — better than fallback
        CURRENT_RATES = { ...FALLBACK_RATES, ...cached.rates };
        LAST_FETCHED = cached.fetchedAt;
        LAST_SOURCE = 'cache';
      }
    }
  } catch (e) { /* localStorage unavailable, ignore */ }
}

export function getRates() {
  return CURRENT_RATES;
}
export function getRate(code) {
  return CURRENT_RATES[code] ?? FALLBACK_RATES[code] ?? null;
}
export function getLastFetched() {
  return LAST_FETCHED;
}
export function getRatesSource() {
  return LAST_SOURCE;
}
export function isCacheStale() {
  if (!LAST_FETCHED) return true;
  return Date.now() - LAST_FETCHED > CACHE_TTL_MS;
}

/**
 * Fetch fresh exchange rates from the public API.
 * @param {object} opts
 * @param {boolean} opts.force - if true, skip the staleness check
 * @returns {Promise<{ok: boolean, fetchedAt?: number, error?: string}>}
 */
export async function fetchLiveRates({ force = false } = {}) {
  if (!force && !isCacheStale()) {
    return { ok: true, fetchedAt: LAST_FETCHED, fromCache: true };
  }
  try {
    const res = await fetch(API_URL, { cache: 'no-store' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    if (json.result !== 'success' || !json.rates) {
      throw new Error('API returned unexpected payload');
    }
    // Merge fresh rates with fallback (so any missing currencies still work)
    const merged = { ...FALLBACK_RATES };
    for (const code of Object.keys(FALLBACK_RATES)) {
      if (typeof json.rates[code] === 'number') {
        merged[code] = json.rates[code];
      }
    }
    CURRENT_RATES = merged;
    LAST_FETCHED = Date.now();
    LAST_SOURCE = 'live';

    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({ rates: CURRENT_RATES, fetchedAt: LAST_FETCHED })
        );
      } catch (e) { /* quota / private mode, fine */ }
    }
    notify();
    return { ok: true, fetchedAt: LAST_FETCHED, fromCache: false };
  } catch (e) {
    return { ok: false, error: String(e?.message || e) };
  }
}

// Display info for currencies. Symbol shown in UI; name shown in dropdown.
export const CURRENCY_INFO = {
  USD: { symbol: '$',    name: 'US Dollar',          flag: '🇺🇸' },
  EUR: { symbol: '€',    name: 'Euro',               flag: '🇪🇺' },
  GBP: { symbol: '£',    name: 'British Pound',      flag: '🇬🇧' },
  CAD: { symbol: 'CA$',  name: 'Canadian Dollar',    flag: '🇨🇦' },
  AUD: { symbol: 'AU$',  name: 'Australian Dollar',  flag: '🇦🇺' },
  NZD: { symbol: 'NZ$',  name: 'New Zealand Dollar', flag: '🇳🇿' },
  JPY: { symbol: '¥',    name: 'Japanese Yen',       flag: '🇯🇵' },
  CHF: { symbol: 'CHF',  name: 'Swiss Franc',        flag: '🇨🇭' },
  SEK: { symbol: 'kr',   name: 'Swedish Krona',      flag: '🇸🇪' },
  NOK: { symbol: 'kr',   name: 'Norwegian Krone',    flag: '🇳🇴' },
  DKK: { symbol: 'kr',   name: 'Danish Krone',       flag: '🇩🇰' },
  PLN: { symbol: 'zł',   name: 'Polish Złoty',       flag: '🇵🇱' },
  CZK: { symbol: 'Kč',   name: 'Czech Koruna',       flag: '🇨🇿' },
  HUF: { symbol: 'Ft',   name: 'Hungarian Forint',   flag: '🇭🇺' },
  RON: { symbol: 'lei',  name: 'Romanian Leu',       flag: '🇷🇴' },
  BGN: { symbol: 'лв',   name: 'Bulgarian Lev',      flag: '🇧🇬' },
  MXN: { symbol: 'MX$',  name: 'Mexican Peso',       flag: '🇲🇽' },
  BRL: { symbol: 'R$',   name: 'Brazilian Real',     flag: '🇧🇷' },
  HKD: { symbol: 'HK$',  name: 'Hong Kong Dollar',   flag: '🇭🇰' },
  SGD: { symbol: 'S$',   name: 'Singapore Dollar',   flag: '🇸🇬' },
  MYR: { symbol: 'RM',   name: 'Malaysian Ringgit',  flag: '🇲🇾' },
  IDR: { symbol: 'Rp',   name: 'Indonesian Rupiah',  flag: '🇮🇩' },
  PHP: { symbol: '₱',    name: 'Philippine Peso',    flag: '🇵🇭' },
  VND: { symbol: '₫',    name: 'Vietnamese Đồng',    flag: '🇻🇳' },
  ILS: { symbol: '₪',    name: 'Israeli Shekel',     flag: '🇮🇱' },
  TRY: { symbol: '₺',    name: 'Turkish Lira',       flag: '🇹🇷' },
  ZAR: { symbol: 'R',    name: 'South African Rand', flag: '🇿🇦' },
  MAD: { symbol: 'DH',   name: 'Moroccan Dirham',    flag: '🇲🇦' },
  INR: { symbol: '₹',    name: 'Indian Rupee',       flag: '🇮🇳' },
};

// Currencies offered in the "Display in" dropdown. Ordered by global usage.
// The user's country currency is always added to this list dynamically.
export const POPULAR_DISPLAY_CURRENCIES = [
  'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'NZD', 'JPY', 'CHF', 'INR', 'MXN', 'BRL',
];

// Backwards-compat: keep EXCHANGE_RATES as a getter-style alias for code that
// imports it directly (it gets the live values now).
export const EXCHANGE_RATES = new Proxy({}, {
  get(_, prop) { return CURRENT_RATES[prop]; },
  ownKeys() { return Object.keys(CURRENT_RATES); },
  getOwnPropertyDescriptor(_, prop) {
    return { enumerable: true, configurable: true, value: CURRENT_RATES[prop] };
  },
});

/**
 * Convert a value between currencies, using live rates if available.
 * @param {number} value
 * @param {string} fromCcy ISO code (e.g. 'USD')
 * @param {string} toCcy   ISO code
 * @returns {number} converted value, or original if either rate is missing
 */
export function convert(value, fromCcy, toCcy) {
  if (fromCcy === toCcy) return value;
  const fromRate = getRate(fromCcy);
  const toRate = getRate(toCcy);
  if (!fromRate || !toRate) return value;  // unknown ccy — pass through
  const inUsd = value / fromRate;
  return inUsd * toRate;
}
